import { useProviderHub } from '@aries/aptos-defi';
import {
  findFirstHealthyMainnetNode,
  getAptosNodeRequestHeaders,
  MAINNET_OFFICIAL_NODE_NAME,
  Network,
} from '@aries/aptos-defi/common';
import { CenterRow, Col, Txt } from '@aries/mobile-core/components';
import { openModal } from '@aries/mobile-core/Smoothy';
import { ScreenInset } from '@aries/mobile-core/utils';
import { useLatest } from '@aries/shared/hooks';
import { getTFunction, Trans } from '@aries/shared/locale';
import { colorConfig, globalToken, useThemeMode } from '@aries/ui-theming';
import React, { useEffect, useState } from 'react';
import { TouchableOpacity, View, ViewStyle } from 'react-native';
import styled from 'styled-components/native';
import { ProfileSelect } from '~/services';
import { IconLogo } from '../../icons';
import {
  IconDiscord,
  IconEarth,
  IconMenu,
  IconNetwork,
  IconOtterSec,
  IconQuestion,
  IconTwtter,
} from './Icons';
import { openLanguageSelect } from './LanguageSelect';
import { openNetworkSelect } from './NetworkSelect';

export const PAGE_HEADER_HEIGHT = 45;

const NETWORK_HEALTH_CHECK_INTERVAL = 60000;

let hasInitNetworkFromParam = false;

export type HeaderProps = {
  headerRight?: string | React.ReactNode;
  headerLeft?: string | React.ReactNode;
  headerStyle?: ViewStyle;
  hasAccount?: boolean;
};

export const Header = React.memo((props: HeaderProps) => {
  const {
    headerRight,
    headerLeft,
    headerStyle,
    hasAccount = true,
  } = props;
  const { mode } = useThemeMode();
  const { colorBgSpotlight } = colorConfig;
  const { currentNetwork, changeNetwork } = useProviderHub();
  const currentNetworkRef = useLatest(currentNetwork);

  const [showNetworkWarning, setShowNetworkWarning] = useState(false);

  useEffect(() => {
    if (hasInitNetworkFromParam) {
      return;
    }
    hasInitNetworkFromParam = true;

    if (typeof window !== 'undefined' && window.location) {
      const searchParams = new URLSearchParams(window.location.search);
      const network = decodeURIComponent(
        searchParams?.get('network') || '',
      );

      const currentNetworkType = currentNetworkRef.current.network;
      if (network === 'testnet') {
        if (currentNetworkType !== Network.TESTNET) {
          changeNetwork('Testnet - NodeReal');
        }
      } else {
        findFirstHealthyMainnetNode().then((nodeName: string) => {
          changeNetwork(nodeName);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { pingUrl, name, apiKey, headers } = currentNetwork;
  useEffect(() => {
    let isCancelled = false;
    const controller = new AbortController();
    const requestHeaders = getAptosNodeRequestHeaders({ apiKey, headers });

    const checkNetworkHealth = async () => {
      try {
        const response = await fetch(pingUrl, {
          method: 'GET',
          headers: requestHeaders,
          signal: controller.signal,
        });

        if (!isCancelled) {
          if (response.status === 401 || response.status === 403) {
            // eslint-disable-next-line no-console
            console.warn(`Auth failed for ${name}`);
          }

          setShowNetworkWarning(!response.ok);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        if (!isCancelled) {
          setShowNetworkWarning(true);
        }
      }
    };

    checkNetworkHealth();

    const interval = setInterval(
      checkNetworkHealth,
      NETWORK_HEALTH_CHECK_INTERVAL,
    );

    return () => {
      isCancelled = true;
      controller.abort();
      clearInterval(interval);
    };
  }, [apiKey, headers, name, pingUrl]);

  return (
    <>
      {showNetworkWarning && (
        <WarningBanner>
          <CenterRow gap={8} style={{ flex: 1 }}>
            <Txt h2 style={{ color: '#fff', textAlign: 'left' }}>
              {`The current network (${
                currentNetwork.name
              }) is currently unavailable or degraded. Please switch to another network for better performance${
                currentNetwork.name === MAINNET_OFFICIAL_NODE_NAME
                  ? ', or wait a few minutes and try again'
                  : ''
              }.`}
            </Txt>
          </CenterRow>
          <CloseButton onPress={() => setShowNetworkWarning(false)}>
            <Txt style={{ color: '#fff', fontSize: 24 }}>×</Txt>
          </CloseButton>
        </WarningBanner>
      )}
      <Container style={headerStyle}>
        <Content>
          {!headerLeft ? (
            <LeftBox>
              <IconLogo size={25} />
              <Txt ml={8}>Aries Markets</Txt>
            </LeftBox>
          ) : (
            headerLeft
          )}
          {hasAccount ? (
            <ProfileSelect
              style={{
                backgroundColor: `${colorBgSpotlight[mode]}`,
              }}
            />
          ) : null}

          {!headerRight ? (
            <RightBox onPress={() => openMenuModal()}>
              <IconMenu style={{ width: 20, height: 14 }} />
            </RightBox>
          ) : (
            headerRight
          )}
        </Content>
      </Container>
    </>
  );
});

const openMenuModal = () => {
  const t = getTFunction();
  openModal({
    showHeader: true,
    renderHeader: () => (
      <CenterRow>
        <Txt h4>
          <Trans i18nkey="Menu" />
        </Txt>
      </CenterRow>
    ),
    renderContent: ({ close }) => (
      <>
        <Col
          style={[
            {
              width: '100%',
              justifyContent: 'center',
              marginLeft: 80,
              marginTop: 130,
              gap: 40,
            } as ViewStyle,
          ]}
        >
          <MenuContainer
            onPress={() => {
              close?.();
              openNetworkSelect();
            }}
          >
            <IconNetwork />
            <Txt h4 c2>
              {t`Network`}
            </Txt>
          </MenuContainer>

          <MenuContainer
            onPress={() => {
              close?.();
              openLanguageSelect();
            }}
          >
            <IconEarth />
            <Txt h4 c2>
              {t`Language`}
            </Txt>
          </MenuContainer>

          <MenuContainer
            onPress={() => {
              const newWindow = window.open(
                'https://docs.ariesmarkets.xyz/aries-markets/',
                '_blank',
                'noopener,noreferrer',
              );
              if (newWindow) {
                newWindow.opener = null;
              }
            }}
          >
            <IconQuestion />
            <Txt h4 c2>{t`Docs`}</Txt>
          </MenuContainer>
        </Col>
        <Col gap={12} ml={80} mt={80}>
          <CenterRow
            gap={15}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <a
              href="https://discord.com/invite/MGNUs7fA33"
              target="_blank"
              rel="noreferrer"
            >
              <IconDiscord style={{ width: 20, height: 20 }} />
            </a>
            <a
              href="https://twitter.com/AriesMarkets"
              target="_blank"
              rel="noreferrer"
            >
              <IconTwtter style={{ width: 20, height: 20 }} />
            </a>
          </CenterRow>
          <Col>
            <CenterRow mb={12}>
              <Txt c3 h2 mr={8}>{t`Audited by:`}</Txt>
              <a href="https://osec.io/" target="_blank" rel="noreferrer">
                <IconOtterSec style={{ width: 19, height: 19 }} />
              </a>
            </CenterRow>
            <Txt
              c3
            >{t`Copyright © ${`${new Date().getFullYear()}`} Aries Markets `}</Txt>
          </Col>
        </Col>
      </>
    ),
    showClose: true,
  });
};

const { colorBgContainer, colorBgSpotlight, padding } = globalToken;
const Container = styled(View)`
  background-color: ${colorBgContainer};
  padding-top: ${ScreenInset.top}px;
  padding-left: ${padding}px;
  padding-right: ${padding}px;
`;

const Content = styled(CenterRow)`
  height: ${PAGE_HEADER_HEIGHT}px;
`;

const LeftBox = styled(View)`
  flex: 1;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const RightBox = styled(TouchableOpacity)`
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 8px;
  margin-left: 8px;
  border-radius: 8;
  background-color: ${colorBgSpotlight};
`;

const MenuContainer = styled(TouchableOpacity)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const WarningBanner = styled(CenterRow)`
  background-color: #ff9800;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const CloseButton = styled(TouchableOpacity)`
  position: absolute;
  top: 12px;
  right: 8px;
  background-color: transparent;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
